import { formatMoney } from '@domain';
import type { AgedBuckets, AgedResponse } from '../api.js';
import type { Translate } from '../i18n/index.js';
import { ink, theme } from '../theme.js';
import { Card } from '../components/ui.js';
import { BarList } from '../components/Chart.js';

/**
 * Who owes us, or who we owe, and for how long.
 *
 * One component for both directions. They are the same table with the names
 * swapped, and written twice the second one would drift.
 *
 * Assembled from the outstanding invoices rather than fetched: Xero's aged
 * reports take a required contactId, so the summary would be one API call per
 * customer. See api/src/domain/aging.ts. The screen says so, because where a
 * figure came from is part of the figure.
 */

const BUCKETS: { key: keyof Omit<AgedBuckets, 'total'>; label: string }[] = [
  { key: 'current', label: 'Not due yet' },
  { key: 'days1to30', label: '1–30 days' },
  { key: 'days31to60', label: '31–60 days' },
  { key: 'days61to90', label: '61–90 days' },
  { key: 'days90plus', label: 'Over 90 days' },
];

export function AgedTable({
  direction,
  data,
  filter,
  onOpen,
  t,
}: {
  direction: 'receivable' | 'payable';
  data: AgedResponse;
  /** Narrows to the names containing it. Empty shows everybody. */
  filter: string;
  /**
   * Open the detail report for one name.
   *
   * Xero's aged detail is one contact per call and nothing else, so this is
   * where that report is reached from. A row with no contact id cannot be
   * drilled into — the contact was deleted in Xero and the invoices
   * outlived it — and is left as plain text rather than offered as a link
   * that would fail.
   */
  onOpen?: (contactId: string, contact: string) => void;
  t: Translate;
}) {
  const owing = direction === 'receivable';
  const money = (value: number) => (value === 0 ? '—' : formatMoney(value));

  /*
    Filtering recomputes the totals rather than leaving the company-wide ones
    under a narrowed list. A total that does not add up to the rows above it
    is the fastest way to lose a reader's trust in a report.
  */
  const needle = filter.trim().toLowerCase();
  const rows =
    needle === ''
      ? data.rows
      : data.rows.filter((row) => row.contact.toLowerCase().includes(needle));

  const totals =
    needle === ''
      ? data.totals
      : rows.reduce(
          (sum, row) => ({
            current: sum.current + row.current,
            days1to30: sum.days1to30 + row.days1to30,
            days31to60: sum.days31to60 + row.days31to60,
            days61to90: sum.days61to90 + row.days61to90,
            days90plus: sum.days90plus + row.days90plus,
            total: sum.total + row.total,
          }),
          { current: 0, days1to30: 0, days31to60: 0, days61to90: 0, days90plus: 0, total: 0 },
        );

  const cell = {
    padding: '9px 12px',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.dividerSoft}`,
  };

  const figure = {
    ...cell,
    textAlign: 'end' as const,
    fontFamily: theme.fontMono,
    whiteSpace: 'nowrap' as const,
    direction: 'ltr' as const,
  };

  const heading = {
    padding: '9px 12px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '.04em',
    color: theme.label,
    borderBottom: `1px solid ${theme.border}`,
    whiteSpace: 'nowrap' as const,
  };

  if (rows.length === 0) {
    return (
      <Card>
        <div style={{ padding: '18px', fontSize: '13px', color: theme.textMuted }}>
          {needle !== ''
            ? t('Nobody here matches that.')
            : owing
              ? t('Nobody owes this company anything right now.')
              : t('This company owes nothing right now.')}
        </div>
      </Card>
    );
  }

  return (
    <>
      {/*
        The shape of the debt before the detail of it.

        One hue getting darker with age, so the ramp itself says "older"
        without a second colour having to mean "bad" — and the table below
        is the same figures for anybody who wants them exactly.
      */}
      <Card style={{ marginBottom: '14px' }}>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
          }}
        >
          {owing ? t('How late it is') : t('How late we are')}
        </div>
        <BarList
          rows={BUCKETS.map((bucket, index) => ({
            label: t(bucket.label),
            value: totals[bucket.key],
            step: index,
          }))}
          currency={data.currency}
          t={t}
        />
      </Card>

    <Card>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...heading, textAlign: 'start' }}>
              {owing ? t('Client') : t('Vendor')}
            </th>
            {BUCKETS.map((bucket) => (
              <th key={bucket.key} style={{ ...heading, textAlign: 'end' }}>
                {t(bucket.label)}
              </th>
            ))}
            <th style={{ ...heading, textAlign: 'end' }}>{t('Total')}</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.contactId || row.contact}>
              <td style={{ ...cell, textAlign: 'start' }}>
                {onOpen && row.contactId ? (
                  <button
                    type="button"
                    onClick={() => onOpen(row.contactId, row.contact)}
                    title={t('Show every document behind this figure')}
                    style={{
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      font: 'inherit',
                      color: ink,
                      fontWeight: 600,
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                      cursor: 'pointer',
                      textAlign: 'start',
                    }}
                  >
                    {row.contact}
                  </button>
                ) : (
                  row.contact
                )}
                {/* Bracketed, because "Gulf Cold Chain Logistics 3" reads as
                    part of the company's name. */}
                <span style={{ color: theme.textFaint, fontSize: '11.5px' }}>
                  {' ('}
                  {row.documents}
                  {')'}
                </span>
              </td>
              {BUCKETS.map((bucket) => (
                <td
                  key={bucket.key}
                  style={{
                    ...figure,
                    // The oldest column is the one somebody has to act on.
                    color:
                      bucket.key === 'days90plus' && row[bucket.key] > 0
                        ? theme.badDeep
                        : theme.text,
                    fontWeight: bucket.key === 'days90plus' && row[bucket.key] > 0 ? 600 : 400,
                  }}
                >
                  {money(row[bucket.key])}
                </td>
              ))}
              <td style={{ ...figure, fontWeight: 600 }}>{formatMoney(row.total)}</td>
            </tr>
          ))}

          <tr>
            <td style={{ ...cell, textAlign: 'start', fontWeight: 700 }}>{t('Total')}</td>
            {BUCKETS.map((bucket) => (
              <td key={bucket.key} style={{ ...figure, fontWeight: 700 }}>
                {money(totals[bucket.key])}
              </td>
            ))}
            <td style={{ ...figure, fontWeight: 700 }}>
              {formatMoney(totals.total)} {data.currency}
            </td>
          </tr>
        </tbody>
      </table>

      <div
        style={{
          padding: '11px 16px',
          fontSize: '11.5px',
          color: theme.textMuted,
          lineHeight: 1.7,
        }}
      >
        {/*
          Counted from the rows on screen, not from everything read.
          "9 unpaid invoices" under a list narrowed to one customer's three
          is the same inconsistency as a total that does not add up.
        */}
        <div>
          {t('As at')} {data.asAt} · {t('from')}{' '}
          {needle === ''
            ? data.documentsRead
            : rows.reduce((sum, row) => sum + row.documents, 0)}{' '}
          {owing ? t('unpaid invoices') : t('unpaid bills')}
        </div>
        {/*
          Written as a label and a count rather than a sentence with a number
          in it. "1 have no due date" is wrong and "1 has" needs a different
          sentence for every count — in two languages, with Arabic agreeing
          differently again at three and at eleven. A label sidesteps all of
          it. See i18n/messages.ts for the cases where it is worth doing
          properly.
        */}
        {/* Only while nothing is narrowed: an invoice with no due date is
            not attributed to a contact here, so under a filter the count
            would be about rows that may not be on screen. */}
        {needle === '' && data.undated > 0 && (
          <div>
            {t('No due date in Xero')}: {data.undated} · {t('shown as not due yet')}
          </div>
        )}
        {data.truncated && (
          <div style={{ color: theme.warnDeep, fontWeight: 600 }}>
            {t('There were more documents than this could read. The total is partial.')}
          </div>
        )}
      </div>
    </Card>
    </>
  );
}
